import { useId, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import type { DeliverySlotTemplate } from '../../data/delivery'
import { motionTokens } from '../../motion/motionTokens'

export type MysteryCadence = 'once' | 'weekly' | 'fortnightly'

export interface MysteryDeliveryOption {
  readonly id: DeliverySlotTemplate['id']
  readonly label: string
  readonly firstDropLabel: string
}

export interface MysteryPlanSelection {
  readonly cadence: MysteryCadence
  readonly deliverySlotId: DeliverySlotTemplate['id']
}

export interface SurpriseBucketOfferProps {
  readonly compositionName: string
  readonly bucketPriceCents: number
  readonly deliveryFeeCents: number
  readonly deliveryOptions: readonly MysteryDeliveryOption[]
  readonly subscriptionDisabled?: boolean
  readonly onConfirm: (selection: MysteryPlanSelection) => void
  readonly onEditBucket: () => void
}

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
})

const cadenceOptions: ReadonlyArray<{
  readonly id: MysteryCadence
  readonly label: string
  readonly description: string
}> = [
  {
    id: 'once',
    label: 'One-off',
    description: 'One bucket, one local delivery',
  },
  {
    id: 'weekly',
    label: 'Every week',
    description: 'A fresh mystery bucket each week',
  },
  {
    id: 'fortnightly',
    label: 'Every fortnight',
    description: 'A fresh mystery bucket every two weeks',
  },
]

function formatMoney(cents: number): string {
  return currencyFormatter.format(cents / 100)
}

function cadenceSummary(cadence: MysteryCadence | null): string {
  switch (cadence) {
    case 'once':
      return 'One-off'
    case 'weekly':
      return 'Every week'
    case 'fortnightly':
      return 'Every fortnight'
    default:
      return 'Choose a plan'
  }
}

function confirmationLabel(
  cadence: MysteryCadence | null,
  hasDeliverySlot: boolean,
): string {
  if (!cadence) return 'Choose a plan to continue'
  if (!hasDeliverySlot) return 'Choose a delivery run'
  if (cadence === 'once') return 'Confirm one-off bucket'
  if (cadence === 'weekly') return 'Confirm weekly plan'
  return 'Confirm fortnightly plan'
}

export function SurpriseBucketOffer({
  compositionName,
  bucketPriceCents,
  deliveryFeeCents,
  deliveryOptions,
  subscriptionDisabled = false,
  onConfirm,
  onEditBucket,
}: SurpriseBucketOfferProps) {
  const reduceMotion = useReducedMotion()
  const subscriptionHelpId = useId()
  const [cadence, setCadence] = useState<MysteryCadence | null>(null)
  const [deliverySlotId, setDeliverySlotId] = useState<
    DeliverySlotTemplate['id'] | null
  >(null)
  const totalPerDropCents = bucketPriceCents + deliveryFeeCents
  const selectedDelivery =
    deliveryOptions.find((option) => option.id === deliverySlotId) ?? null
  const canConfirm = cadence !== null && selectedDelivery !== null
  const optionHover = reduceMotion ? undefined : { y: -2 }
  const optionTap = reduceMotion ? undefined : { scale: 0.98 }

  function confirmSelection() {
    if (!cadence || !selectedDelivery) return

    onConfirm({
      cadence,
      deliverySlotId: selectedDelivery.id,
    })
  }

  return (
    <motion.section
      className="surprise-offer surprise-offer--plan-selector"
      aria-labelledby="surprise-heading"
      initial={
        reduceMotion
          ? { opacity: 0 }
          : { opacity: 0, y: 24, scale: 0.97 }
      }
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={
        reduceMotion
          ? { duration: motionTokens.reducedOpacity }
          : { duration: motionTokens.standard, ease: 'easeOut' }
      }
    >
      <p className="surprise-offer__eyebrow">
        Your Sub Zero guide &middot; plan your drop
      </p>
      <h2 id="surprise-heading">How often should we drop the surprise?</h2>
      <p>
        Pick a rhythm and local delivery run. We&rsquo;ll show the exact price
        and first drop before you confirm.
      </p>

      <fieldset className="surprise-offer__cadence bucket-options">
        <legend>Choose one-off or a subscription</legend>
        {cadenceOptions.map((option) => {
          const recurring = option.id !== 'once'
          const disabled = recurring && subscriptionDisabled

          return (
            <motion.button
              type="button"
              key={option.id}
              className={
                cadence === option.id
                  ? 'surprise-offer__choice is-selected'
                  : 'surprise-offer__choice'
              }
              aria-pressed={cadence === option.id}
              aria-describedby={disabled ? subscriptionHelpId : undefined}
              disabled={disabled}
              whileHover={optionHover}
              whileTap={optionTap}
              transition={
                reduceMotion ? { duration: 0 } : motionTokens.spring
              }
              onClick={() => setCadence(option.id)}
            >
              <span>{option.label}</span>
              <small>
                {option.description} &middot;{' '}
                {formatMoney(totalPerDropCents)}
                {recurring ? ' per drop' : ' total'}
              </small>
            </motion.button>
          )
        })}
      </fieldset>

      {subscriptionDisabled ? (
        <p
          id={subscriptionHelpId}
          className="surprise-offer__subscription-note bucket-notice"
        >
          Gift buckets stay one-off so the recipient can choose their own
          recurring plan later.
        </p>
      ) : null}

      <fieldset className="surprise-offer__delivery bucket-options">
        <legend>Choose your local delivery run</legend>
        {deliveryOptions.map((option) => (
          <motion.button
            type="button"
            key={option.id}
            className={
              selectedDelivery?.id === option.id
                ? 'surprise-offer__choice is-selected'
                : 'surprise-offer__choice'
            }
            aria-pressed={selectedDelivery?.id === option.id}
            whileHover={optionHover}
            whileTap={optionTap}
            transition={reduceMotion ? { duration: 0 } : motionTokens.spring}
            onClick={() => setDeliverySlotId(option.id)}
          >
            <span>{option.label}</span>
            <small>First drop: {option.firstDropLabel}</small>
          </motion.button>
        ))}
      </fieldset>

      {deliveryOptions.length === 0 ? (
        <p className="bucket-notice" role="status">
          No delivery runs are available for this postcode yet.
        </p>
      ) : null}

      <div
        className="surprise-offer__equation"
        data-testid="surprise-plan-equation"
        aria-label={`${formatMoney(bucketPriceCents)} bucket plus ${formatMoney(
          deliveryFeeCents,
        )} delivery equals ${formatMoney(totalPerDropCents)} ${
          cadence === 'once' ? 'for this drop' : 'per drop'
        }`}
      >
        <span>{formatMoney(bucketPriceCents)}</span>
        <span aria-hidden="true">+</span>
        <span>{formatMoney(deliveryFeeCents)}</span>
        <span aria-hidden="true">=</span>
        <strong>
          {formatMoney(totalPerDropCents)}{' '}
          {cadence === 'once' ? 'for this drop' : 'per drop'}
        </strong>
      </div>

      <dl
        className="surprise-offer__summary"
        data-testid="surprise-plan-summary"
      >
        <div>
          <dt>Bucket</dt>
          <dd>{compositionName}</dd>
        </div>
        <div>
          <dt>Plan</dt>
          <dd>{cadenceSummary(cadence)}</dd>
        </div>
        <div>
          <dt>Delivery run</dt>
          <dd>{selectedDelivery?.label ?? 'Choose a run'}</dd>
        </div>
        <div>
          <dt>First drop</dt>
          <dd>{selectedDelivery?.firstDropLabel ?? 'Choose a run'}</dd>
        </div>
        <div>
          <dt>Order cut-off</dt>
          <dd>Wednesday, 12:00 pm AEST</dd>
        </div>
      </dl>

      <p className="surprise-offer__controls-note">
        Weekly and fortnightly plans can be skipped, paused or cancelled before
        the Wednesday noon cut-off. Later changes apply to the following drop.
      </p>
      <p className="surprise-offer__terms">
        Clickable prototype only. No payment, order, delivery or subscription
        will be created.
      </p>

      <div className="bucket-reveal__actions surprise-offer__actions">
        <button
          className="bucket-button bucket-button--primary"
          type="button"
          disabled={!canConfirm}
          onClick={confirmSelection}
        >
          {confirmationLabel(cadence, selectedDelivery !== null)}
        </button>
        <button
          className="bucket-text-button surprise-offer__edit"
          type="button"
          onClick={onEditBucket}
        >
          Edit my bucket
        </button>
      </div>
    </motion.section>
  )
}
